/**
 * Red Team YAML Synchronization & Mapping Engine.
 *
 * Provides bidirectional translation between the Visual Form state
 * in RedTeamingPage.tsx and the canonical RedTeamConfig schema.
 *
 * Preserves unmapped / advanced configuration fields (such as
 * execution.budget, execution.rate_limit, and output.gate) during round-trips.
 */

export interface TargetAuthenticationConfig {
  type: string;
  token?: string;
  key?: string;
  value?: string;
  location?: "header" | "query";
  username?: string;
  password?: string;
}

export interface TargetConfig {
  type: "http" | "langchain" | "websocket" | "ws" | "ai_provider" | "python" | "javascript" | "go" | "a2a" | "claude-agent" | "claude-agent-sdk" | "mcp" | string;
  name?: string;
  endpoint?: string;
  method?: string;
  timeout?: number;
  headers?: Record<string, string>;
  authentication?: TargetAuthenticationConfig;
  protocols?: string[];
  app_id?: string;
  owner?: string;
  body_template?: string;
  response_path?: string;
  input_key?: string;
  response_key?: string;
  provider?: string;
  model?: string;
  credentials?: Record<string, any>;
  credential_ref?: string;
  api_key_env?: string;
  base_url?: string;
  parameters?: Record<string, any>;
  timeout_seconds?: number;
  region?: string;
  deployment_name?: string;
  api_version?: string;
  api_key?: string;
  apiKey?: string;

  // Script target configurations (Python, JavaScript/TypeScript, Go)
  file_path?: string;
  filePath?: string;
  config_json?: string;
  configJson?: string;
  test_generation_instructions?: string;
  testGenerationInstructions?: string;
  delay_ms?: number | string;
  delayMs?: number | string;
  extension_hook?: string;
  extensionHook?: string;
  prompts?: string[];

  // A2A / Claude Agent SDK / MCP target configurations
  provider_id?: string;
  providerId?: string;
  agent_card_url?: string;
  agentCardUrl?: string;
  endpoint_url?: string;
  endpointUrl?: string;
  working_dir?: string;
  workingDir?: string;
  server_config?: string;
  serverConfig?: string;
  fallback_model?: string;
  fallbackModel?: string;
  copy_working_dir?: boolean | string;
  copyWorkingDir?: boolean | string;
  api_key_required?: boolean;
  apiKeyRequired?: boolean;
  max_turns?: number;
  maxTurns?: number;
  max_thinking_tokens?: number;
  maxThinkingTokens?: number;
  max_budget_usd?: number;
  maxBudgetUsd?: number;
  task_budget?: { total?: number };
  taskBudget?: { total?: number };
  permission_mode?: string;
  permissionMode?: string;
  allow_dangerously_skip_permissions?: boolean;
  allowDangerouslySkipPermissions?: boolean;
  custom_system_prompt?: string;
  customSystemPrompt?: string;
  append_system_prompt?: string;
  appendSystemPrompt?: string;
  exclude_dynamic_sections?: boolean;
  excludeDynamicSections?: boolean;
  tools?: string[] | Record<string, any>;
  custom_allowed_tools?: string[];
  customAllowedTools?: string[];
  append_allowed_tools?: string[];
  appendAllowedTools?: string[];
  disallowed_tools?: string[];
  disallowedTools?: string[];
  allow_all_tools?: boolean;
  allowAllTools?: boolean;
  additional_directories?: string[];
  additionalDirectories?: string[];
  mcp?: Record<string, any>;
  strict_mcp_config?: boolean;
  skills?: string[] | string;
  setting_sources?: string[];
  settingSources?: string[];

  // Open Interpreter target configurations
  auto_run?: boolean;
  autoRun?: boolean;
  safe_mode?: string;
  safeMode?: string;
  max_output?: number | string;
  maxOutput?: number | string;
  system_message?: string;
  systemMessage?: string;
  api_base?: string;
  apiBase?: string;

  // Local model configurations (Ollama, llama.cpp, Llamafile, LocalAI, Text Generation WebUI, vLLM)
  host?: string;
  server_url?: string;
  serverUrl?: string;
  api_base_url?: string;
  apiBaseUrl?: string;
  num_predict?: number;
  numPredict?: number;
  num_ctx?: number;
  numCtx?: number;
  n_predict?: number;
  nPredict?: number;
  repeat_penalty?: number;
  repeatPenalty?: number;
  top_k?: number;
  topK?: number;
  top_p?: number;
  topP?: number;
  keep_alive?: string | number;
  keepAlive?: string | number;
  stop?: string[] | string;
  mode?: string;
  config?: Record<string, any>;

  // AWS Bedrock Agent configuration
  agent_id?: string;
  agentId?: string;
  agent_alias_id?: string;
  agentAliasId?: string;
  session_id?: string;
  sessionId?: string;
  memory_id?: string;
  memoryId?: string;
  enable_trace?: boolean;
  enableTrace?: boolean;
  end_session?: boolean;
  endSession?: boolean;
  session_attributes?: Record<string, string>;
  sessionAttributes?: Record<string, string>;
  prompt_session_attributes?: Record<string, string>;
  promptSessionAttributes?: Record<string, string>;
  aws_profile?: string;
  awsProfile?: string;
  aws_access_key_id?: string;
  awsAccessKeyId?: string;
  aws_secret_access_key?: string;
  awsSecretAccessKey?: string;
  aws_session_token?: string;
  awsSessionToken?: string;

  // Executable / Shell Command target configuration
  command?: string;
  env?: Record<string, string>;

  // Custom & Other Agent Framework fields
  connection_type?: string;
  connectionType?: string;
  framework?: string;
  [key: string]: any;
}

import type { CustomTargetConfigValues } from "./CustomTargetConfiguration";
import { DEFAULT_CUSTOM_TARGET_CONFIG } from "./CustomTargetConfiguration";
export type { CustomTargetConfigValues };
export { DEFAULT_CUSTOM_TARGET_CONFIG };

import type { OtherAgentTargetConfigValues } from "./OtherAgentTargetConfiguration";
import { DEFAULT_OTHER_AGENT_CONFIG } from "./OtherAgentTargetConfiguration";
export type { OtherAgentTargetConfigValues };
export { DEFAULT_OTHER_AGENT_CONFIG };

import type { ExecutableTargetConfigValues } from "./ExecutableTargetConfiguration";
import {
  DEFAULT_EXECUTABLE_TARGET_CONFIG,
  EXECUTABLE_EXAMPLE_CONFIG,
  validateExecutableTargetConfig,
} from "./ExecutableTargetConfiguration";
export type { ExecutableTargetConfigValues };
export {
  DEFAULT_EXECUTABLE_TARGET_CONFIG,
  EXECUTABLE_EXAMPLE_CONFIG,
  validateExecutableTargetConfig,
};

import type { LocalModelTargetConfigValues, LocalModelProviderType } from "./LocalModelTargetConfiguration";
import {
  DEFAULT_OLLAMA_TARGET_CONFIG,
  DEFAULT_LLAMACPP_TARGET_CONFIG,
  DEFAULT_LLAMAFILE_TARGET_CONFIG,
  DEFAULT_LOCALAI_TARGET_CONFIG,
  DEFAULT_TEXTGEN_WEBUI_TARGET_CONFIG,
  DEFAULT_VLLM_TARGET_CONFIG,
} from "./LocalModelTargetConfiguration";
export type { LocalModelTargetConfigValues, LocalModelProviderType };
export {
  DEFAULT_OLLAMA_TARGET_CONFIG,
  DEFAULT_LLAMACPP_TARGET_CONFIG,
  DEFAULT_LLAMAFILE_TARGET_CONFIG,
  DEFAULT_LOCALAI_TARGET_CONFIG,
  DEFAULT_TEXTGEN_WEBUI_TARGET_CONFIG,
  DEFAULT_VLLM_TARGET_CONFIG,
};

import type { BedrockAgentTargetConfigValues } from "./BedrockAgentTargetConfiguration";
import {
  DEFAULT_BEDROCK_AGENT_TARGET_CONFIG,
  validateBedrockAgentTargetConfig,
} from "./BedrockAgentTargetConfiguration";
export type { BedrockAgentTargetConfigValues };
export {
  DEFAULT_BEDROCK_AGENT_TARGET_CONFIG,
  validateBedrockAgentTargetConfig,
};

export interface OpenInterpreterTargetConfigValues {
  model: string;
  apiBase?: string;
  apiKey?: string;
  autoRun: boolean;
  safeMode: "off" | "ask" | "auto";
  maxOutput?: number | string;
  systemMessage?: string;
  configJson: string;
  testGenerationInstructions: string;
  delayMs: number | string;
  extensionHook: string;
  prompts: string[];
}

export { DEFAULT_OPEN_INTERPRETER_TARGET_CONFIG } from "./OpenInterpreterTargetConfiguration";

export interface ScriptTargetConfigValues {
  filePath: string;
  configJson: string;
  testGenerationInstructions: string;
  delayMs: number | string;
  extensionHook: string;
  prompts: string[];
  pythonExecutable?: string;
  env?: Record<string, string>;
  workingDir?: string;
  timeout?: number | string;
}

export type A2AAuthType = "none" | "bearer" | "api_key" | "basic";

export interface A2ATargetConfigValues {
  providerId: string;
  agentCardUrl: string;
  endpointUrl: string;
  authType: A2AAuthType;
  bearerToken: string;
  apiKeyName: string;
  apiKeyValue: string;
  basicUser: string;
  basicPass: string;
  configJson: string;
  testGenerationInstructions: string;
  delayMs: number | string;
  extensionHook: string;
}

export interface ClaudeAgentTargetConfigValues {
  providerId: string;
  workingDir: string;
  copyWorkingDir?: boolean;
  model: string;
  fallbackModel?: string;
  apiKey: string;
  apiKeyRequired?: boolean;
  maxTurns?: number | string;
  maxThinkingTokens?: number | string;
  maxBudgetUsd?: number | string;
  tokenBudget?: number | string;
  permissionMode?: string;
  allowDangerouslySkipPermissions?: boolean;
  customSystemPrompt?: string;
  appendSystemPrompt?: string;
  excludeDynamicSections?: boolean;
  toolsMode?: "preset" | "custom" | "disabled";
  allowAllTools?: boolean;
  customAllowedTools?: string[];
  appendAllowedTools?: string[];
  disallowedTools?: string[];
  additionalDirectories?: string[];
  mcpJson?: string;
  skills?: string[];
  settingSources?: string[];
  configJson: string;
  testGenerationInstructions: string;
  delayMs: number | string;
  extensionHook: string;
  prompts: string[];
}

export interface MCPTargetConfigValues {
  serverConfig: string;
  configJson: string;
  testGenerationInstructions: string;
  delayMs: number | string;
  extensionHook: string;
  prompts: string[];
}

export interface ApplicationConfig {
  purpose: string;
  description?: string;
  system_prompt?: string;
  data_sensitivity?: "low" | "medium" | "high";
}

export interface ModelConfig {
  provider: string;
  name: string;
  credential_ref?: string;
  base_url?: string;
  temperature?: number;
  max_tokens?: number;
  timeout?: number;
  overrides?: Record<string, Record<string, any>>;
}

export interface StrategyItemConfig {
  id: string;
  config?: Record<string, any>;
}

export interface RetryConfig {
  max_attempts?: number;
  backoff_s?: number;
}

export interface RateLimitConfig {
  requests_per_minute?: number;
}

export interface BudgetConfig {
  max_usd?: number;
  on_exceeded?: "stop" | "warn";
}

export interface ExecutionConfig {
  concurrency?: number;
  timeout_s?: number;
  max_requests?: number;
  retry?: RetryConfig;
  rate_limit?: RateLimitConfig;
  budget?: BudgetConfig;
}

export interface GateConfig {
  fail_on_severity?: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
}

export interface OutputConfig {
  formats?: string[];
  gate?: GateConfig;
}

export interface RedTeamConfigDict {
  schema_version: string;
  extends?: string | null;
  target: TargetConfig;
  application?: ApplicationConfig;
  model?: ModelConfig;
  plugins?: string[];
  strategies?: StrategyItemConfig[];
  execution?: ExecutionConfig;
  output?: OutputConfig;
}

export interface RedTeamFormState {
  targetType: "http" | "langchain" | "websocket" | "ws" | "ai_provider" | "python" | "javascript" | "go" | "a2a" | "claude-agent" | "claude-agent-sdk" | "mcp" | string;
  name: string;
  endpoint: string;
  httpMethod: string;
  timeout: number;
  protocols?: string[];
  customHeaders: Array<{ key: string; value: string }>;
  authType: string;
  apiKey: string;
  apiVal: string;
  apiKeyLoc: "header" | "query";
  bearerToken: string;
  basicUser: string;
  basicPass: string;
  bodyTemplate: string;
  responsePath: string;
  inputKey: string;
  responseKey: string;

  // AI Provider Target
  aiProvider?: string;
  aiModel?: string;
  aiCredentialType?: "env" | "direct" | "env_var" | "credential_ref";
  aiApiKey?: string;
  aiApiKeyEnv?: string;
  aiCredentialRef?: string;
  aiBaseUrl?: string;
  aiTemperature?: number;
  aiMaxTokens?: number;
  aiTopP?: number;
  aiTimeout?: number;
  aiAzureDeployment?: string;
  aiAzureApiVersion?: string;
  aiAwsRegion?: string;

  // Application
  appName: string;
  appPurpose: string;
  appAdditionalContext: string;

  // Model
  modelProvider: string;
  modelName: string;
  modelApiKey: string;
  modelBaseUrl: string;
  modelTemperature: number;
  modelMaxTokens: number;
  modelTimeout: number;

  // Plugins
  selectedPlugins: Record<string, boolean>;

  // Strategies
  selectedStrategy: string;
  crescendoMaxTurns: number;
  hydraBranches: number;
  hydraDepth: number;
  mischievousMaxTurns: number;
  goatMaxTurns: number;
  jailbreakSample: number;
  layerSteps: string[];
  unicodeMode: string;
  bestOfN: number;
  treeBranches: number;
  treeDepth: number;
  customTemplate: string;
  maxRetries: number;
  gcgNumCandidates: number;
  gcgCustomSuffix: string;
  indirectFormat: string;
  citationStyle: string;

  // Target Selection / Card ID
  selectedCardId?: string;

  // Script Providers (Python, JavaScript/TypeScript, Go)
  pythonConfig?: ScriptTargetConfigValues;
  javascriptConfig?: ScriptTargetConfigValues;
  goConfig?: ScriptTargetConfigValues;

  // Agent / Framework Providers
  a2aConfig?: A2ATargetConfigValues;
  claudeAgentConfig?: ClaudeAgentTargetConfigValues;
  mcpConfig?: MCPTargetConfigValues;
  openInterpreterConfig?: OpenInterpreterTargetConfigValues;

  // Local Model Providers
  ollamaConfig?: LocalModelTargetConfigValues;
  llamaCppConfig?: LocalModelTargetConfigValues;
  llamafileConfig?: LocalModelTargetConfigValues;
  localaiConfig?: LocalModelTargetConfigValues;
  textGenWebuiConfig?: LocalModelTargetConfigValues;
  vllmConfig?: LocalModelTargetConfigValues;

  // AWS Bedrock Agent Provider
  bedrockAgentConfig?: BedrockAgentTargetConfigValues;

  // Executable / Shell Command Provider
  executableConfig?: ExecutableTargetConfigValues;

  // Custom Target Provider
  customTargetConfig?: CustomTargetConfigValues;

  // Other Agent Framework Provider
  otherAgentConfig?: OtherAgentTargetConfigValues;

  // Execution & Budget Settings
  budgetLimit?: number;
  concurrency?: number;
}

/**
 * Builds a StrategyItemConfig matching the UI selection and parameters.
 */
export function buildStrategyConfig(form: RedTeamFormState): StrategyItemConfig {
  const id = form.selectedStrategy || "basic";
  switch (id) {
    case "crescendo":
      return { id: "crescendo", config: { max_turns: form.crescendoMaxTurns } };
    case "hydra":
      return { id: "hydra", config: { max_branches: form.hydraBranches, max_depth: form.hydraDepth } };
    case "mischievous-user":
    case "mischievous_user":
      return { id: "mischievous-user", config: { max_turns: form.mischievousMaxTurns } };
    case "goat":
      return { id: "goat", config: { max_turns: form.goatMaxTurns } };
    case "jailbreak-templates":
      return { id: "jailbreak-templates", config: { sample: form.jailbreakSample } };
    case "layer":
      return { id: "layer", config: { steps: form.layerSteps } };
    case "unicode":
      return { id: "unicode", config: { mode: form.unicodeMode } };
    case "best-of-n":
      return { id: "best-of-n", config: { n: form.bestOfN } };
    case "tree-jailbreak":
      return { id: "tree-jailbreak", config: { branches: form.treeBranches, depth: form.treeDepth } };
    case "custom":
      return { id: "custom", config: { template: form.customTemplate } };
    case "retry":
      return { id: "retry", config: { max_retries: form.maxRetries } };
    case "gcg":
      return {
        id: "gcg",
        config: {
          num_candidates: form.gcgNumCandidates,
          ...(form.gcgCustomSuffix ? { custom_suffix: form.gcgCustomSuffix } : {}),
        },
      };
    case "indirect-web-pwn":
      return { id: "indirect-web-pwn", config: { format: form.indirectFormat } };
    case "citation":
      return { id: "citation", config: { style: form.citationStyle } };
    default:
      return { id, config: {} };
  }
}

/**
 * Resolves the canonical target type string from current form state.
 * Accurately determines target type using both selectedCardId and targetType.
 */
export function resolveCanonicalTargetType(form: RedTeamFormState): string {
  const customMap: Record<string, string> = {
    python: "python",
    llamaindex: "llamaindex",
    "llama-index": "llamaindex",
    llama_index: "llamaindex",
    langgraph: "langgraph",
    "lang-graph": "langgraph",
    lang_graph: "langgraph",
    autogen: "autogen",
    "auto-gen": "autogen",
    auto_gen: "autogen",
    crewai: "crewai",
    "crew-ai": "crewai",
    crew_ai: "crewai",
    "openai-agents": "openai-agents",
    openai_agents: "openai-agents",
    "openai-agent": "openai-agents",
    openai_agent: "openai-agents",
    "google-adk": "google-adk",
    google_adk: "google-adk",
    "google-genai": "google-adk",
    google_genai: "google-adk",
    javascript: "javascript",
    go: "go",
    a2a: "a2a",
    "claude-agent": "claude-agent",
    "claude-agent-sdk": "claude-agent",
    mcp: "mcp",
    "open-interpreter": "open-interpreter",
    open_interpreter: "open-interpreter",
    openinterpreter: "open-interpreter",
    ollama: "ollama",
    llamacpp: "llamacpp",
    "llama-cpp": "llamacpp",
    "llama.cpp": "llamacpp",
    llamafile: "llamafile",
    localai: "localai",
    "text-generation-webui": "text-generation-webui",
    vllm: "vllm",
    "bedrock-agent": "bedrock-agent",
    "bedrock-agents": "bedrock-agent",
    bedrock_agent: "bedrock-agent",
    bedrock_agents: "bedrock-agent",
    shell: "shell",
    exec: "shell",
    "custom-script": "shell",
    "custom_script": "shell",
    executable: "shell",
    websocket: "websocket",
    ws: "websocket",
    langchain: "langchain",
    ai_provider: "ai_provider",
  };

  if (form.selectedCardId && customMap[form.selectedCardId]) {
    return customMap[form.selectedCardId];
  }
  if (form.targetType && customMap[form.targetType]) {
    return customMap[form.targetType];
  }

  const AI_PROVIDER_IDS = new Set([
    "openai", "anthropic", "azure", "gemini", "bedrock", "mistral",
    "groq", "cohere", "vertex", "together", "deepseek", "perplexity", "replicate",
    "fal", "fireworks", "huggingface", "hyperbolic", "openrouter", "voyage",
    "grok", "watsonx", "cloudflare", "github-models", "ai_provider"
  ]);

  if (
    form.targetType === "ai_provider" ||
    form.selectedCardId === "ai_provider" ||
    (form.selectedCardId && AI_PROVIDER_IDS.has(form.selectedCardId)) ||
    (Boolean(form.aiProvider) && form.targetType !== "websocket" && !customMap[form.selectedCardId || ""])
  ) {
    return "ai_provider";
  }

  return form.targetType || "http";
}

/**
 * Updates or merges current Form state into a canonical RedTeamConfigDict.
 * Non-visual configuration keys (such as budget, rate_limit, output.gate) are preserved.
 */
export function formStateToCanonical(
  form: RedTeamFormState,
  baseCanonical?: RedTeamConfigDict | null
): RedTeamConfigDict {
  // Convert customHeaders array to Record<string, string>
  const headersRecord: Record<string, string> = {};
  if (Array.isArray(form.customHeaders)) {
    for (const h of form.customHeaders) {
      if (h && h.key && h.key.trim()) {
        headersRecord[h.key.trim()] = h.value ?? "";
      }
    }
  }

  // Build target authentication block
  const authConfig: TargetAuthenticationConfig = {
    type: form.authType || "none",
  };
  if (form.authType === "bearer" && form.bearerToken) {
    authConfig.token = form.bearerToken;
  } else if (form.authType === "api_key") {
    authConfig.key = form.apiKey || undefined;
    authConfig.value = form.apiVal || undefined;
    authConfig.location = form.apiKeyLoc || "header";
  } else if (form.authType === "basic") {
    authConfig.username = form.basicUser || undefined;
    authConfig.password = form.basicPass || undefined;
  }

  // Selected plugins list
  const activePlugins = Object.keys(form.selectedPlugins || {}).filter(
    (k) => form.selectedPlugins[k]
  );

  const strategyItem = buildStrategyConfig(form);

  return {
    schema_version: baseCanonical?.schema_version || "1.0",
    ...(baseCanonical?.extends ? { extends: baseCanonical.extends } : {}),
    target: (() => {
      const targetType = resolveCanonicalTargetType(form);
      const isSameType = Boolean(
        baseCanonical?.target?.type &&
          (baseCanonical.target.type === targetType ||
            (targetType === "claude-agent" && baseCanonical.target.type === "claude-agent-sdk") ||
            (targetType === "websocket" && baseCanonical.target.type === "ws") ||
            (targetType === "llamacpp" && (baseCanonical.target.type === "llama-cpp" || baseCanonical.target.type === "llama.cpp")) ||
            (targetType === "bedrock-agent" && (baseCanonical.target.type === "bedrock_agent" || baseCanonical.target.type === "bedrock-agents")) ||
            (targetType === "shell" &&
              (baseCanonical.target.type === "exec" ||
                baseCanonical.target.type === "custom-script" ||
                baseCanonical.target.type === "custom_script" ||
                baseCanonical.target.type === "executable")) ||
            (targetType === "open-interpreter" &&
              (baseCanonical.target.type === "open_interpreter" ||
                baseCanonical.target.type === "openinterpreter")))
      );
      const activeBase = isSameType ? baseCanonical?.target : undefined;
      const name = (form.name || form.appName || "").trim() || activeBase?.name;

      if (targetType === "ai_provider") {
        return {
          type: "ai_provider",
          ...(name ? { name } : {}),
          provider: form.aiProvider || "openai",
          model: form.aiModel || "deepseek-v4-flash",
          ...(form.aiApiKeyEnv ? { api_key_env: form.aiApiKeyEnv, credentials: { api_key_env: form.aiApiKeyEnv } } : {}),
          ...(form.aiCredentialRef ? { credential_ref: form.aiCredentialRef } : {}),
          ...(form.aiBaseUrl ? { base_url: form.aiBaseUrl } : {}),
          ...(form.aiAzureDeployment ? { deployment_name: form.aiAzureDeployment } : {}),
          ...(form.aiAzureApiVersion ? { api_version: form.aiAzureApiVersion } : {}),
          ...(form.aiAwsRegion ? { region: form.aiAwsRegion } : {}),
          timeout: form.aiTimeout ?? form.timeout ?? 60,
          timeout_seconds: form.aiTimeout ?? form.timeout ?? 60,
          ...((form.aiTemperature !== undefined || form.aiMaxTokens !== undefined || form.aiTopP !== undefined)
            ? {
                parameters: {
                  ...(form.aiTemperature !== undefined ? { temperature: form.aiTemperature } : {}),
                  ...(form.aiMaxTokens !== undefined ? { max_tokens: form.aiMaxTokens } : {}),
                  ...(form.aiTopP !== undefined ? { top_p: form.aiTopP } : {}),
                },
              }
            : {}),
        };
      }

      if (
        targetType === "python" ||
        targetType === "llamaindex" ||
        targetType === "langgraph" ||
        targetType === "autogen" ||
        targetType === "crewai" ||
        targetType === "openai-agents" ||
        targetType === "google-adk" ||
        targetType === "javascript" ||
        targetType === "go"
      ) {
        const isPythonFramework = (
          targetType === "python" ||
          targetType === "llamaindex" ||
          targetType === "langgraph" ||
          targetType === "autogen" ||
          targetType === "crewai" ||
          targetType === "openai-agents" ||
          targetType === "google-adk"
        );
        const scriptCfg = isPythonFramework
          ? form.pythonConfig
          : targetType === "javascript"
          ? form.javascriptConfig
          : form.goConfig;

        const defaultPath =
          targetType === "llamaindex"
            ? "file://./llamaindex_agent.py"
            : targetType === "langgraph"
            ? "file://./langgraph_agent.py"
            : targetType === "autogen"
            ? "file://./autogen_agent.py"
            : targetType === "crewai"
            ? "file://./crewai_agent.py"
            : targetType === "openai-agents"
            ? "file://./openai_agent.py"
            : targetType === "google-adk"
            ? "file://./google_adk_agent.py"
            : targetType === "python"
            ? (activeBase?.file_path || "/path/to/custom_provider.py")
            : targetType === "javascript"
            ? "/path/to/custom_provider.js"
            : "/path/to/your/script";

        const resolvedPath = scriptCfg?.filePath || activeBase?.file_path || activeBase?.filePath || defaultPath;
        const providerId = resolvedPath.startsWith("file://")
          ? resolvedPath
          : (isPythonFramework ? (resolvedPath.startsWith("/") || resolvedPath.startsWith(".") ? `file://${resolvedPath}` : resolvedPath) : resolvedPath);

        let parsedConfig: Record<string, any> = {};
        if (scriptCfg?.configJson && scriptCfg.configJson.trim() !== "" && scriptCfg.configJson.trim() !== "{}") {
          try {
            parsedConfig = JSON.parse(scriptCfg.configJson);
          } catch {
            // keep empty on syntax error
          }
        }

        const promptfooConfig: Record<string, any> = {
          ...(scriptCfg?.pythonExecutable ? { pythonExecutable: scriptCfg.pythonExecutable } : activeBase?.python_executable ? { pythonExecutable: activeBase.python_executable } : {}),
          ...parsedConfig,
        };

        return {
          type: targetType,
          ...(name ? { name } : {}),
          provider_id: providerId,
          file_path: resolvedPath,
          ...(scriptCfg?.pythonExecutable ? { python_executable: scriptCfg.pythonExecutable } : activeBase?.python_executable ? { python_executable: activeBase.python_executable } : {}),
          ...(Object.keys(promptfooConfig).length > 0 ? { config: promptfooConfig, parameters: promptfooConfig } : {}),
          ...(scriptCfg?.configJson ? { config_json: scriptCfg.configJson } : activeBase?.config_json ? { config_json: activeBase.config_json } : {}),
          ...(scriptCfg?.testGenerationInstructions ? { test_generation_instructions: scriptCfg.testGenerationInstructions } : activeBase?.test_generation_instructions ? { test_generation_instructions: activeBase.test_generation_instructions } : {}),
          delay_ms: scriptCfg?.delayMs ?? activeBase?.delay_ms ?? 0,
          ...(scriptCfg?.extensionHook ? { extension_hook: scriptCfg.extensionHook } : activeBase?.extension_hook ? { extension_hook: activeBase.extension_hook } : {}),
          prompts: scriptCfg?.prompts || activeBase?.prompts || ["{{prompt}}"],
          timeout: scriptCfg?.timeout ? Number(scriptCfg.timeout) : (form.timeout || 60),
        };
      }

      if (targetType === "a2a") {
        const a2a = form.a2aConfig;
        const a2aAuth: TargetAuthenticationConfig = {
          type: a2a?.authType || activeBase?.authentication?.type || "none",
        };
        if (a2aAuth.type === "bearer") {
          a2aAuth.token = a2a?.bearerToken || activeBase?.authentication?.token || "";
        } else if (a2aAuth.type === "api_key") {
          a2aAuth.key = a2a?.apiKeyName || activeBase?.authentication?.key || "X-API-Key";
          a2aAuth.value = a2a?.apiKeyValue || activeBase?.authentication?.value || "";
          a2aAuth.location = "header";
        } else if (a2aAuth.type === "basic") {
          a2aAuth.username = a2a?.basicUser || activeBase?.authentication?.username || "";
          a2aAuth.password = a2a?.basicPass || activeBase?.authentication?.password || "";
        }

        return {
          type: "a2a",
          ...(name ? { name } : {}),
          provider_id: a2a?.providerId || activeBase?.provider_id || "a2a",
          agent_card_url: a2a?.agentCardUrl || activeBase?.agent_card_url || "",
          ...(a2a?.endpointUrl ? { endpoint_url: a2a.endpointUrl } : activeBase?.endpoint_url ? { endpoint_url: activeBase.endpoint_url } : {}),
          authentication: a2aAuth,
          ...(a2a?.configJson ? { config_json: a2a.configJson } : activeBase?.config_json ? { config_json: activeBase.config_json } : {}),
          ...(a2a?.testGenerationInstructions ? { test_generation_instructions: a2a.testGenerationInstructions } : activeBase?.test_generation_instructions ? { test_generation_instructions: activeBase.test_generation_instructions } : {}),
          delay_ms: a2a?.delayMs ?? activeBase?.delay_ms ?? 0,
          ...(a2a?.extensionHook ? { extension_hook: a2a.extensionHook } : activeBase?.extension_hook ? { extension_hook: activeBase.extension_hook } : {}),
          timeout: form.timeout || 60,
        };
      }

      if (targetType === "claude-agent" || targetType === "claude-agent-sdk") {
        const claude = form.claudeAgentConfig;
        const maxTurnsNum = claude?.maxTurns !== undefined && claude?.maxTurns !== "" ? Number(claude.maxTurns) : activeBase?.max_turns;
        const maxThinkingTokensNum = claude?.maxThinkingTokens !== undefined && claude?.maxThinkingTokens !== "" ? Number(claude.maxThinkingTokens) : activeBase?.max_thinking_tokens;
        const maxBudgetUsdNum = claude?.maxBudgetUsd !== undefined && claude?.maxBudgetUsd !== "" ? Number(claude.maxBudgetUsd) : activeBase?.max_budget_usd;
        const tokenBudgetNum = claude?.tokenBudget !== undefined && claude?.tokenBudget !== "" ? Number(claude.tokenBudget) : undefined;

        let mcpObj: Record<string, any> | undefined = activeBase?.mcp;
        if (claude?.mcpJson && claude.mcpJson.trim()) {
          try {
            mcpObj = JSON.parse(claude.mcpJson);
          } catch {
            // Keep existing if parse error
          }
        }

        let toolsVal: any = activeBase?.tools;
        if (claude?.toolsMode === "disabled") {
          toolsVal = [];
        } else if (claude?.toolsMode === "custom") {
          toolsVal = claude.customAllowedTools || (claude as any)?.toolsList;
        }

        return {
          type: "claude-agent",
          ...(name ? { name } : {}),
          provider_id: claude?.providerId || activeBase?.provider_id || "anthropic:claude-agent-sdk",
          working_dir: claude?.workingDir || activeBase?.working_dir || "./",
          ...(claude?.copyWorkingDir !== undefined ? { copy_working_dir: claude.copyWorkingDir } : activeBase?.copy_working_dir !== undefined ? { copy_working_dir: activeBase.copy_working_dir } : {}),
          model: claude?.model || activeBase?.model || "claude-3-5-sonnet-20241022",
          ...(claude?.fallbackModel ? { fallback_model: claude.fallbackModel } : activeBase?.fallback_model ? { fallback_model: activeBase.fallback_model } : {}),
          ...(claude?.apiKeyRequired !== undefined ? { api_key_required: claude.apiKeyRequired } : activeBase?.api_key_required !== undefined ? { api_key_required: activeBase.api_key_required } : {}),
          ...(claude?.apiKey ? { credentials: { api_key: claude.apiKey } } : activeBase?.credentials ? { credentials: activeBase.credentials } : {}),
          ...(maxTurnsNum !== undefined && !isNaN(maxTurnsNum) ? { max_turns: maxTurnsNum } : {}),
          ...(maxThinkingTokensNum !== undefined && !isNaN(maxThinkingTokensNum) ? { max_thinking_tokens: maxThinkingTokensNum } : {}),
          ...(maxBudgetUsdNum !== undefined && !isNaN(maxBudgetUsdNum) ? { max_budget_usd: maxBudgetUsdNum } : {}),
          ...(tokenBudgetNum !== undefined && !isNaN(tokenBudgetNum) ? { task_budget: { total: tokenBudgetNum } } : activeBase?.task_budget ? { task_budget: activeBase.task_budget } : {}),
          ...(claude?.permissionMode ? { permission_mode: claude.permissionMode } : activeBase?.permission_mode ? { permission_mode: activeBase.permission_mode } : {}),
          ...(claude?.allowDangerouslySkipPermissions !== undefined ? { allow_dangerously_skip_permissions: claude.allowDangerouslySkipPermissions } : activeBase?.allow_dangerously_skip_permissions !== undefined ? { allow_dangerously_skip_permissions: activeBase.allow_dangerously_skip_permissions } : {}),
          ...(claude?.customSystemPrompt ? { custom_system_prompt: claude.customSystemPrompt } : activeBase?.custom_system_prompt ? { custom_system_prompt: activeBase.custom_system_prompt } : {}),
          ...(claude?.appendSystemPrompt ? { append_system_prompt: claude.appendSystemPrompt } : activeBase?.append_system_prompt ? { append_system_prompt: activeBase.append_system_prompt } : {}),
          ...(claude?.excludeDynamicSections !== undefined ? { exclude_dynamic_sections: claude.excludeDynamicSections } : activeBase?.exclude_dynamic_sections !== undefined ? { exclude_dynamic_sections: activeBase.exclude_dynamic_sections } : {}),
          ...(toolsVal !== undefined ? { tools: toolsVal } : {}),
          ...(claude?.allowAllTools !== undefined ? { allow_all_tools: claude.allowAllTools } : activeBase?.allow_all_tools !== undefined ? { allow_all_tools: activeBase.allow_all_tools } : {}),
          ...(claude?.customAllowedTools?.length ? { custom_allowed_tools: claude.customAllowedTools } : activeBase?.custom_allowed_tools ? { custom_allowed_tools: activeBase.custom_allowed_tools } : {}),
          ...(claude?.appendAllowedTools?.length ? { append_allowed_tools: claude.appendAllowedTools } : activeBase?.append_allowed_tools ? { append_allowed_tools: activeBase.append_allowed_tools } : {}),
          ...(claude?.disallowedTools?.length ? { disallowed_tools: claude.disallowedTools } : activeBase?.disallowed_tools ? { disallowed_tools: activeBase.disallowed_tools } : {}),
          ...(claude?.additionalDirectories?.length ? { additional_directories: claude.additionalDirectories } : activeBase?.additional_directories ? { additional_directories: activeBase.additional_directories } : {}),
          ...(mcpObj ? { mcp: mcpObj } : {}),
          ...(claude?.skills?.length ? { skills: claude.skills } : activeBase?.skills ? { skills: activeBase.skills } : {}),
          ...(claude?.settingSources?.length ? { setting_sources: claude.settingSources } : activeBase?.setting_sources ? { setting_sources: activeBase.setting_sources } : {}),
          ...(claude?.configJson ? { config_json: claude.configJson } : activeBase?.config_json ? { config_json: activeBase.config_json } : {}),
          ...(claude?.testGenerationInstructions ? { test_generation_instructions: claude.testGenerationInstructions } : activeBase?.test_generation_instructions ? { test_generation_instructions: activeBase.test_generation_instructions } : {}),
          delay_ms: claude?.delayMs ?? activeBase?.delay_ms ?? 0,
          ...(claude?.extensionHook ? { extension_hook: claude.extensionHook } : activeBase?.extension_hook ? { extension_hook: activeBase.extension_hook } : {}),
          prompts: claude?.prompts || activeBase?.prompts || ["{{prompt}}"],
          timeout: form.timeout || 60,
        };
      }

      if (targetType === "mcp") {
        const mcp = form.mcpConfig;
        return {
          type: "mcp",
          ...(name ? { name } : {}),
          server_config: mcp?.serverConfig || activeBase?.server_config || "mcp",
          ...(mcp?.configJson ? { config_json: mcp.configJson } : activeBase?.config_json ? { config_json: activeBase.config_json } : {}),
          ...(mcp?.testGenerationInstructions ? { test_generation_instructions: mcp.testGenerationInstructions } : activeBase?.test_generation_instructions ? { test_generation_instructions: activeBase.test_generation_instructions } : {}),
          delay_ms: mcp?.delayMs ?? activeBase?.delay_ms ?? 0,
          ...(mcp?.extensionHook ? { extension_hook: mcp.extensionHook } : activeBase?.extension_hook ? { extension_hook: activeBase.extension_hook } : {}),
          prompts: mcp?.prompts || activeBase?.prompts || ["{{prompt}}"],
          timeout: form.timeout || 60,
        };
      }

      if (
        targetType === "open-interpreter" ||
        targetType === "open_interpreter" ||
        targetType === "openinterpreter"
      ) {
        const oi = form.openInterpreterConfig;
        return {
          type: "open-interpreter",
          ...(name ? { name } : {}),
          model: oi?.model || activeBase?.model || "gpt-4o",
          ...(oi?.apiBase ? { api_base: oi.apiBase } : activeBase?.api_base ? { api_base: activeBase.api_base } : {}),
          ...(oi?.apiKey ? { credentials: { api_key: oi.apiKey } } : activeBase?.credentials ? { credentials: activeBase.credentials } : {}),
          auto_run: oi?.autoRun !== undefined ? oi.autoRun : activeBase?.auto_run ?? true,
          safe_mode: oi?.safeMode || activeBase?.safe_mode || "off",
          ...(oi?.maxOutput !== undefined ? { max_output: oi.maxOutput } : activeBase?.max_output !== undefined ? { max_output: activeBase.max_output } : {}),
          ...(oi?.systemMessage ? { system_message: oi.systemMessage } : activeBase?.system_message ? { system_message: activeBase.system_message } : {}),
          ...(oi?.configJson ? { config_json: oi.configJson } : activeBase?.config_json ? { config_json: activeBase.config_json } : {}),
          ...(oi?.testGenerationInstructions ? { test_generation_instructions: oi.testGenerationInstructions } : activeBase?.test_generation_instructions ? { test_generation_instructions: activeBase.test_generation_instructions } : {}),
          delay_ms: oi?.delayMs ?? activeBase?.delay_ms ?? 0,
          ...(oi?.extensionHook ? { extension_hook: oi.extensionHook } : activeBase?.extension_hook ? { extension_hook: activeBase.extension_hook } : {}),
          prompts: oi?.prompts || activeBase?.prompts || ["{{prompt}}"],
          timeout: form.timeout || 60,
        };
      }

      if (
        targetType === "ollama" ||
        targetType === "llamacpp" ||
        targetType === "llama-cpp" ||
        targetType === "llamafile" ||
        targetType === "localai" ||
        targetType === "text-generation-webui" ||
        targetType === "vllm"
      ) {
        const localCfg =
          targetType === "ollama"
            ? form.ollamaConfig
            : targetType === "llamacpp" || targetType === "llama-cpp"
            ? form.llamaCppConfig
            : targetType === "llamafile"
            ? form.llamafileConfig
            : targetType === "localai"
            ? form.localaiConfig
            : targetType === "text-generation-webui"
            ? form.textGenWebuiConfig
            : form.vllmConfig;

        let parsedJson: Record<string, any> = {};
        if (localCfg?.configJson && localCfg.configJson.trim() !== "") {
          try {
            parsedJson = JSON.parse(localCfg.configJson);
          } catch {
            // Keep empty on syntax error
          }
        }

        const serverUrl =
          localCfg?.serverUrl ||
          parsedJson.host ||
          parsedJson.apiBaseUrl ||
          (targetType === "ollama"
            ? "http://localhost:11434"
            : targetType === "llamacpp" || targetType === "llama-cpp"
            ? "http://localhost:8080/completion"
            : targetType === "llamafile"
            ? "http://localhost:8080/v1"
            : targetType === "localai"
            ? "http://localhost:8080/v1"
            : targetType === "text-generation-webui"
            ? "http://localhost:5000/v1"
            : "http://localhost:8000/v1");

        const rawModel =
          localCfg?.modelName?.trim() ||
          (targetType === "ollama"
            ? "ollama:llama3"
            : targetType === "llamacpp" || targetType === "llama-cpp"
            ? "llama.cpp:http://localhost:8080/completion"
            : targetType === "llamafile"
            ? "LLaMA_CPP"
            : targetType === "localai"
            ? "localai:chat:gpt-4"
            : targetType === "text-generation-webui"
            ? "default"
            : "meta-llama/Llama-2-7b-chat-hf");

        const cleanModel = rawModel.replace(
          /^(ollama:(chat:|completion:|embeddings:)?|llama\.cpp:|localai:(chat:|completion:)?|openai:(chat:|completion:)?)/,
          ""
        );
        const pMode =
          localCfg?.providerMode ||
          (rawModel.includes(":completion:")
            ? "completion"
            : rawModel.includes(":embeddings:")
            ? "embeddings"
            : "chat");

        // Format Promptfoo documented provider_id
        let providerId = rawModel;
        if (targetType === "ollama") {
          providerId = rawModel.startsWith("ollama:")
            ? rawModel
            : `ollama:${pMode}:${cleanModel || "llama3.2"}`;
        } else if (targetType === "llamacpp" || targetType === "llama-cpp") {
          providerId =
            rawModel.startsWith("llama.cpp:") || rawModel.startsWith("llama:")
              ? rawModel
              : `llama.cpp:${serverUrl}`;
        } else if (targetType === "llamafile") {
          providerId = rawModel.startsWith("openai:")
            ? rawModel
            : `openai:${pMode}:${cleanModel || "LLaMA_CPP"}`;
        } else if (targetType === "localai") {
          providerId = rawModel.startsWith("localai:")
            ? rawModel
            : `localai:${pMode}:${cleanModel || "gpt-4"}`;
        } else if (targetType === "text-generation-webui") {
          providerId = rawModel.startsWith("openai:")
            ? rawModel
            : `openai:${pMode}:${cleanModel || "default"}`;
        } else if (targetType === "vllm") {
          providerId =
            rawModel.startsWith("openai:") || rawModel.startsWith("vllm:")
              ? rawModel
              : `openai:${pMode}:${cleanModel || "meta-llama/Llama-2-7b-chat-hf"}`;
        }

        const normalizedLocalType =
          targetType === "llama-cpp" ? "llamacpp" : targetType;

        const tempVal =
          localCfg?.temperature !== undefined && localCfg?.temperature !== ""
            ? Number(localCfg.temperature)
            : parsedJson.temperature;
        const topPVal =
          localCfg?.topP !== undefined && localCfg?.topP !== ""
            ? Number(localCfg.topP)
            : parsedJson.top_p;
        const topKVal =
          localCfg?.topK !== undefined && localCfg?.topK !== ""
            ? Number(localCfg.topK)
            : parsedJson.top_k;
        const numPredictVal =
          localCfg?.numPredict !== undefined && localCfg?.numPredict !== ""
            ? Number(localCfg.numPredict)
            : parsedJson.num_predict;
        const numCtxVal =
          localCfg?.numCtx !== undefined && localCfg?.numCtx !== ""
            ? Number(localCfg.numCtx)
            : parsedJson.num_ctx;
        const nPredictVal =
          localCfg?.nPredict !== undefined && localCfg?.nPredict !== ""
            ? Number(localCfg.nPredict)
            : parsedJson.n_predict;
        const maxTokensVal =
          localCfg?.maxTokens !== undefined && localCfg?.maxTokens !== ""
            ? Number(localCfg.maxTokens)
            : parsedJson.max_tokens;
        const repeatPenaltyVal =
          localCfg?.repeatPenalty !== undefined && localCfg?.repeatPenalty !== ""
            ? Number(localCfg.repeatPenalty)
            : parsedJson.repeat_penalty;
        const keepAliveVal = localCfg?.keepAlive || parsedJson.keep_alive;
        const stopSeqVal = localCfg?.stopSequences
          ? Array.isArray(localCfg.stopSequences)
            ? localCfg.stopSequences
            : localCfg.stopSequences
                .split(",")
                .map((s: string) => s.trim())
                .filter(Boolean)
          : parsedJson.stop;
        const modeVal = localCfg?.generationMode || parsedJson.mode;
        const instructionTemplateVal =
          localCfg?.instructionTemplate || parsedJson.instruction_template;
        const thinkingVal =
          localCfg?.thinkingMode !== undefined
            ? localCfg.thinkingMode
            : parsedJson.thinking;
        const apiKeyVal = localCfg?.apiKey || parsedJson.apiKey;
        const apiKeyEnvVal = localCfg?.apiKeyEnv || parsedJson.apiKeyEnv;

        const mergedConfig: Record<string, any> = {
          ...parsedJson,
          ...(targetType === "ollama" ? { host: serverUrl } : { apiBaseUrl: serverUrl }),
          ...(tempVal !== undefined && !isNaN(tempVal) ? { temperature: tempVal } : {}),
          ...(topPVal !== undefined && !isNaN(topPVal) ? { top_p: topPVal } : {}),
          ...(topKVal !== undefined && !isNaN(topKVal) ? { top_k: topKVal } : {}),
          ...(numPredictVal !== undefined && !isNaN(numPredictVal)
            ? { num_predict: numPredictVal }
            : {}),
          ...(numCtxVal !== undefined && !isNaN(numCtxVal) ? { num_ctx: numCtxVal } : {}),
          ...(nPredictVal !== undefined && !isNaN(nPredictVal) ? { n_predict: nPredictVal } : {}),
          ...(maxTokensVal !== undefined && !isNaN(maxTokensVal)
            ? { max_tokens: maxTokensVal }
            : {}),
          ...(repeatPenaltyVal !== undefined && !isNaN(repeatPenaltyVal)
            ? { repeat_penalty: repeatPenaltyVal }
            : {}),
          ...(keepAliveVal ? { keep_alive: keepAliveVal } : {}),
          ...(stopSeqVal ? { stop: stopSeqVal } : {}),
          ...(modeVal ? { mode: modeVal } : {}),
          ...(instructionTemplateVal
            ? { instruction_template: instructionTemplateVal }
            : {}),
          ...(thinkingVal !== undefined ? { thinking: thinkingVal } : {}),
          ...(apiKeyVal ? { apiKey: apiKeyVal } : {}),
        };

        return {
          type: normalizedLocalType,
          ...(name ? { name } : {}),
          provider_id: providerId,
          model: cleanModel,
          endpoint: serverUrl,
          host: serverUrl,
          base_url: serverUrl,
          server_url: serverUrl,
          provider_mode: pMode,
          ...(apiKeyVal ? { api_key: apiKeyVal } : {}),
          ...(apiKeyEnvVal ? { api_key_env: apiKeyEnvVal } : {}),
          ...(tempVal !== undefined && !isNaN(tempVal) ? { temperature: tempVal } : {}),
          ...(topPVal !== undefined && !isNaN(topPVal) ? { top_p: topPVal } : {}),
          ...(topKVal !== undefined && !isNaN(topKVal) ? { top_k: topKVal } : {}),
          ...(numPredictVal !== undefined && !isNaN(numPredictVal)
            ? { num_predict: numPredictVal }
            : {}),
          ...(numCtxVal !== undefined && !isNaN(numCtxVal) ? { num_ctx: numCtxVal } : {}),
          ...(nPredictVal !== undefined && !isNaN(nPredictVal) ? { n_predict: nPredictVal } : {}),
          ...(maxTokensVal !== undefined && !isNaN(maxTokensVal)
            ? { max_tokens: maxTokensVal }
            : {}),
          ...(repeatPenaltyVal !== undefined && !isNaN(repeatPenaltyVal)
            ? { repeat_penalty: repeatPenaltyVal }
            : {}),
          ...(keepAliveVal ? { keep_alive: keepAliveVal } : {}),
          ...(stopSeqVal ? { stop: stopSeqVal } : {}),
          ...(modeVal ? { mode: modeVal } : {}),
          ...(instructionTemplateVal
            ? { instruction_template: instructionTemplateVal }
            : {}),
          ...(thinkingVal !== undefined ? { thinking: thinkingVal } : {}),
          config: mergedConfig,
          parameters: mergedConfig,
          ...(localCfg?.configJson && localCfg.configJson.trim() !== "{}"
            ? { config_json: localCfg.configJson }
            : activeBase?.config_json
            ? { config_json: activeBase.config_json }
            : {}),
          ...(localCfg?.testGenerationInstructions
            ? { test_generation_instructions: localCfg.testGenerationInstructions }
            : activeBase?.test_generation_instructions
            ? { test_generation_instructions: activeBase.test_generation_instructions }
            : {}),
          delay_ms: localCfg?.delayMs ?? activeBase?.delay_ms ?? 0,
          ...(localCfg?.extensionHook
            ? { extension_hook: localCfg.extensionHook }
            : activeBase?.extension_hook
            ? { extension_hook: activeBase.extension_hook }
            : {}),
          prompts: localCfg?.prompts || activeBase?.prompts || ["{{prompt}}"],
          timeout: form.timeout || 60,
        };
      }

      if (
        targetType === "bedrock-agent" ||
        targetType === "bedrock_agent" ||
        targetType === "bedrock-agents"
      ) {
        const ba = form.bedrockAgentConfig;
        const agentId = ba?.agentId?.trim() || activeBase?.agent_id || activeBase?.agentId || "";
        const agentAliasId = ba?.agentAliasId?.trim() || activeBase?.agent_alias_id || activeBase?.agentAliasId || "TSTALIASID";
        const region = ba?.region?.trim() || activeBase?.region || "us-east-1";
        const providerId = `bedrock-agent:${agentId}`;

        let parsedConfig: Record<string, any> = {};
        if (ba?.configJson && ba.configJson.trim() !== "" && ba.configJson.trim() !== "{}") {
          try {
            parsedConfig = JSON.parse(ba.configJson);
          } catch {
            // Keep empty on syntax error
          }
        }

        let sessionAttributes: Record<string, string> | undefined;
        if (ba?.sessionAttributesJson && ba.sessionAttributesJson.trim() !== "" && ba.sessionAttributesJson.trim() !== "{}") {
          try {
            sessionAttributes = JSON.parse(ba.sessionAttributesJson);
          } catch {
            // ignore
          }
        } else if (activeBase?.session_attributes) {
          sessionAttributes = activeBase.session_attributes;
        }

        let promptSessionAttributes: Record<string, string> | undefined;
        if (ba?.promptSessionAttributesJson && ba.promptSessionAttributesJson.trim() !== "" && ba.promptSessionAttributesJson.trim() !== "{}") {
          try {
            promptSessionAttributes = JSON.parse(ba.promptSessionAttributesJson);
          } catch {
            // ignore
          }
        } else if (activeBase?.prompt_session_attributes) {
          promptSessionAttributes = activeBase.prompt_session_attributes;
        }

        const promptfooConfig: Record<string, any> = {
          agentAliasId,
          region,
          ...(ba?.enableTrace !== undefined ? { enableTrace: ba.enableTrace } : activeBase?.enable_trace !== undefined ? { enableTrace: activeBase.enable_trace } : {}),
          ...(ba?.sessionId ? { sessionId: ba.sessionId } : activeBase?.session_id ? { sessionId: activeBase.session_id } : {}),
          ...(ba?.memoryId ? { memoryId: ba.memoryId } : activeBase?.memory_id ? { memoryId: activeBase.memory_id } : {}),
          ...(ba?.endSession !== undefined ? { endSession: ba.endSession } : activeBase?.end_session !== undefined ? { endSession: activeBase.end_session } : {}),
          ...(sessionAttributes ? { sessionAttributes } : {}),
          ...(promptSessionAttributes ? { promptSessionAttributes } : {}),
          ...(ba?.authMethod === "profile" && ba?.awsProfile ? { profile: ba.awsProfile } : activeBase?.aws_profile ? { profile: activeBase.aws_profile } : {}),
          ...parsedConfig,
        };

        return {
          type: "bedrock-agent",
          ...(name ? { name } : {}),
          provider_id: providerId,
          agent_id: agentId,
          agent_alias_id: agentAliasId,
          region,
          ...(ba?.enableTrace !== undefined ? { enable_trace: ba.enableTrace } : activeBase?.enable_trace !== undefined ? { enable_trace: activeBase.enable_trace } : {}),
          ...(ba?.sessionId ? { session_id: ba.sessionId } : activeBase?.session_id ? { session_id: activeBase.session_id } : {}),
          ...(ba?.memoryId ? { memory_id: ba.memoryId } : activeBase?.memory_id ? { memory_id: activeBase.memory_id } : {}),
          ...(ba?.endSession !== undefined ? { end_session: ba.endSession } : activeBase?.end_session !== undefined ? { end_session: activeBase.end_session } : {}),
          ...(sessionAttributes ? { session_attributes: sessionAttributes } : {}),
          ...(promptSessionAttributes ? { prompt_session_attributes: promptSessionAttributes } : {}),
          ...(ba?.authMethod === "profile" && ba?.awsProfile ? { aws_profile: ba.awsProfile } : activeBase?.aws_profile ? { aws_profile: activeBase.aws_profile } : {}),
          config: promptfooConfig,
          parameters: promptfooConfig,
          ...(ba?.configJson && ba.configJson.trim() !== "{}" ? { config_json: ba.configJson } : activeBase?.config_json ? { config_json: activeBase.config_json } : {}),
          ...(ba?.testGenerationInstructions ? { test_generation_instructions: ba.testGenerationInstructions } : activeBase?.test_generation_instructions ? { test_generation_instructions: activeBase.test_generation_instructions } : {}),
          delay_ms: ba?.delayMs ?? activeBase?.delay_ms ?? 0,
          ...(ba?.extensionHook ? { extension_hook: ba.extensionHook } : activeBase?.extension_hook ? { extension_hook: activeBase.extension_hook } : {}),
          prompts: ba?.prompts || activeBase?.prompts || ["{{prompt}}"],
          timeout: ba?.timeout ? Number(ba.timeout) : form.timeout || 60,
        };
      }

      if (
        targetType === "shell" ||
        targetType === "exec" ||
        targetType === "custom-script" ||
        targetType === "custom_script" ||
        targetType === "executable"
      ) {
        const execCfg = form.executableConfig;
        const rawCmd =
          execCfg?.command?.trim() ||
          activeBase?.command ||
          activeBase?.executable ||
          "exec:python /path/to/agent.py";
        const providerId = rawCmd.startsWith("exec:") ? rawCmd : `exec:${rawCmd}`;
        const cmdOnly = rawCmd.startsWith("exec:") ? rawCmd.slice(5).trim() : rawCmd;

        let parsedConfig: Record<string, any> = {};
        if (
          execCfg?.configJson &&
          execCfg.configJson.trim() !== "" &&
          execCfg.configJson.trim() !== "{}"
        ) {
          try {
            parsedConfig = JSON.parse(execCfg.configJson);
          } catch {
            // Keep empty on syntax error
          }
        }

        const env = execCfg?.env || activeBase?.env || {};
        const timeoutVal =
          execCfg?.timeout !== undefined && execCfg.timeout !== ""
            ? Number(execCfg.timeout)
            : (form.timeout || 30000);

        const promptfooConfig: Record<string, any> = {
          ...(Object.keys(env).length > 0 ? { env } : {}),
          ...(timeoutVal ? { timeout: timeoutVal } : {}),
          ...parsedConfig,
        };

        return {
          type: "shell",
          ...(name ? { name } : {}),
          command: cmdOnly,
          provider_id: providerId,
          ...(Object.keys(env).length > 0 ? { env } : {}),
          config: promptfooConfig,
          parameters: promptfooConfig,
          ...(execCfg?.configJson && execCfg.configJson.trim() !== "{}"
            ? { config_json: execCfg.configJson }
            : activeBase?.config_json
            ? { config_json: activeBase.config_json }
            : {}),
          ...(execCfg?.testGenerationInstructions
            ? { test_generation_instructions: execCfg.testGenerationInstructions }
            : activeBase?.test_generation_instructions
            ? { test_generation_instructions: activeBase.test_generation_instructions }
            : {}),
          delay_ms: execCfg?.delayMs ?? activeBase?.delay_ms ?? 0,
          ...(execCfg?.extensionHook
            ? { extension_hook: execCfg.extensionHook }
            : activeBase?.extension_hook
            ? { extension_hook: activeBase.extension_hook }
            : {}),
          prompts: execCfg?.prompts || activeBase?.prompts || ["{{prompt}}"],
          timeout: timeoutVal,
        };
      }

      if (targetType === "websocket") {
        return {
          type: "websocket",
          ...(name ? { name } : {}),
          endpoint: form.endpoint || "",
          timeout: form.timeout || 60,
          headers: headersRecord,
          authentication: authConfig,
          ...(form.protocols && form.protocols.length > 0 ? { protocols: form.protocols } : activeBase?.protocols ? { protocols: activeBase.protocols } : {}),
          ...(form.bodyTemplate ? { body_template: form.bodyTemplate } : {}),
          ...(form.responsePath ? { response_path: form.responsePath } : {}),
        };
      }

      if (targetType === "langchain") {
        return {
          type: "langchain",
          ...(name ? { name } : {}),
          endpoint: form.endpoint || "langchain://local_agent",
          timeout: form.timeout || 60,
          headers: headersRecord,
          authentication: authConfig,
          ...(form.inputKey ? { input_key: form.inputKey } : {}),
          ...(form.responseKey ? { response_key: form.responseKey } : {}),
        };
      }

      if (targetType === "custom" || form.selectedCardId === "custom") {
        const customCfg = form.customTargetConfig;
        const connMethod = customCfg?.connectionMethod || "http";
        if (connMethod === "python") {
          return {
            type: "custom",
            connection_type: "python",
            ...(name ? { name } : {}),
            file_path: customCfg?.filePath || "file://./agent.py",
            function_name: customCfg?.functionName || "call_api",
            python_executable: customCfg?.pythonExecutable || "python3",
            working_dir: customCfg?.workingDir || undefined,
            config_json: customCfg?.configJson || "{}",
            env: customCfg?.env || {},
            timeout: customCfg?.timeout ? Number(customCfg.timeout) : (form.timeout || 60),
          };
        } else if (connMethod === "custom_app") {
          return {
            type: "custom",
            connection_type: "executable",
            ...(name ? { name } : {}),
            command: customCfg?.command || "exec:python /path/to/agent.py",
            working_dir: customCfg?.workingDir || undefined,
            config_json: customCfg?.configJson || "{}",
            env: customCfg?.env || {},
            timeout: customCfg?.timeout ? Number(customCfg.timeout) : (form.timeout || 60),
          };
        } else {
          return {
            type: "custom",
            connection_type: "http",
            ...(name ? { name } : {}),
            endpoint: customCfg?.endpoint || form.endpoint || "",
            method: customCfg?.method || form.httpMethod || "POST",
            timeout: customCfg?.timeout ? Number(customCfg.timeout) : (form.timeout || 60),
            headers: customCfg?.headers ? Object.fromEntries(customCfg.headers.filter((h: any) => h.key.trim()).map((h: any) => [h.key.trim(), h.value])) : headersRecord,
            authentication: customCfg ? {
              type: customCfg.authType,
              token: customCfg.authType === "bearer" ? customCfg.bearerToken : undefined,
              key: customCfg.authType === "api_key" ? customCfg.apiKey : undefined,
              value: customCfg.authType === "api_key" ? customCfg.apiVal : undefined,
              location: customCfg.authType === "api_key" ? customCfg.apiKeyLoc : undefined,
              username: customCfg.authType === "basic" ? customCfg.basicUser : undefined,
              password: customCfg.authType === "basic" ? customCfg.basicPass : undefined,
            } : authConfig,
            body_template: customCfg?.bodyTemplate || form.bodyTemplate || undefined,
            response_path: customCfg?.responsePath || form.responsePath || undefined,
          };
        }
      }

      if (targetType === "other-agent" || form.selectedCardId === "other-agent") {
        const otherCfg = form.otherAgentConfig;
        return {
          type: "other-agent",
          framework: otherCfg?.framework || "langchain",
          ...(name ? { name } : {}),
          file_path: otherCfg?.filePath || "file://./agent.py",
          function_name: otherCfg?.functionName || "call_api",
          app_id: otherCfg?.appId || undefined,
          endpoint: otherCfg?.endpoint || undefined,
          command: otherCfg?.command || undefined,
          python_executable: otherCfg?.pythonExecutable || "python3",
          working_dir: otherCfg?.workingDir || undefined,
          config_json: otherCfg?.configJson || "{}",
          env: otherCfg?.env || {},
          timeout: otherCfg?.timeout ? Number(otherCfg.timeout) : (form.timeout || 60),
        };
      }

      return {
        type: "http",
        ...(name ? { name } : {}),
        endpoint: form.endpoint || "",
        method: form.httpMethod || "POST",
        timeout: form.timeout || 60,
        headers: headersRecord,
        authentication: authConfig,
        ...(form.protocols && form.protocols.length > 0 ? { protocols: form.protocols } : activeBase?.protocols ? { protocols: activeBase.protocols } : {}),
        ...(form.bodyTemplate ? { body_template: form.bodyTemplate } : {}),
        ...(form.responsePath ? { response_path: form.responsePath } : {}),
        ...(activeBase?.app_id ? { app_id: activeBase.app_id } : {}),
        ...(activeBase?.owner ? { owner: activeBase.owner } : {}),
      };
    })(),
    application: {
      purpose: form.appPurpose || "General AI Application",
      ...(form.appName ? { description: form.appName } : form.name ? { description: form.name } : {}),
      ...(form.appAdditionalContext ? { system_prompt: form.appAdditionalContext } : {}),
      ...(baseCanonical?.application?.data_sensitivity
        ? { data_sensitivity: baseCanonical.application.data_sensitivity }
        : {}),
    },
    model: {
      provider: form.modelProvider || "openai",
      name: form.modelName || "deepseek-v4-flash",
      ...(form.modelApiKey ? { credential_ref: form.modelApiKey } : {}),
      ...(form.modelBaseUrl ? { base_url: form.modelBaseUrl } : {}),
      temperature: form.modelTemperature ?? 0.7,
      max_tokens: form.modelMaxTokens ?? 4096,
      timeout: form.modelTimeout ?? 60,
      ...(baseCanonical?.model?.overrides ? { overrides: baseCanonical.model.overrides } : {}),
    },
    plugins: activePlugins,
    strategies: [strategyItem],
    execution: {
      concurrency: form.concurrency ?? baseCanonical?.execution?.concurrency ?? 2,
      timeout_s: form.timeout ? Math.max(form.timeout, 120) : baseCanonical?.execution?.timeout_s ?? 120,
      max_requests: baseCanonical?.execution?.max_requests ?? 500,
      retry: {
        max_attempts: form.maxRetries ?? baseCanonical?.execution?.retry?.max_attempts ?? 2,
        ...(baseCanonical?.execution?.retry?.backoff_s !== undefined
          ? { backoff_s: baseCanonical.execution.retry.backoff_s }
          : {}),
      },
      ...(form.budgetLimit !== undefined
        ? {
            budget: {
              max_usd: form.budgetLimit,
              on_exceeded: (baseCanonical?.execution?.budget?.on_exceeded as any) || "stop",
            },
          }
        : baseCanonical?.execution?.budget
        ? { budget: baseCanonical.execution.budget }
        : {}),
      ...(baseCanonical?.execution?.rate_limit
        ? { rate_limit: baseCanonical.execution.rate_limit }
        : {}),
    },
    output: {
      formats: baseCanonical?.output?.formats || ["json"],
      ...(baseCanonical?.output?.gate ? { gate: baseCanonical.output.gate } : {}),
    },
  };
}

/**
 * Hydrates partial form state from a canonical RedTeamConfigDict returned by the validator.
 */
export function canonicalToFormState(
  config: RedTeamConfigDict
): Partial<RedTeamFormState> {
  const partial: Partial<RedTeamFormState> = {};

  const rawTarget =
    config.target ||
    (Array.isArray((config as any)?.targets) ? (config as any).targets[0] : undefined) ||
    (Array.isArray((config as any)?.providers) ? (config as any).providers[0] : undefined);

  let target = rawTarget;
  if (typeof rawTarget === "string") {
    target = {
      id: rawTarget,
      provider_id: rawTarget,
      type: rawTarget.startsWith("exec:") ? "shell" : rawTarget,
    };
  }

  if (target) {
    if (target.type === "ai_provider" || target.provider) {
      partial.targetType = "ai_provider";
      partial.selectedCardId = target.provider || "ai_provider";
      partial.aiProvider = target.provider || "openai";
      partial.aiModel = target.model || "deepseek-v4-flash";
      partial.aiApiKeyEnv = target.api_key_env || target.credentials?.api_key_env || `${(target.provider || "openai").toUpperCase()}_API_KEY`;
      partial.aiCredentialType = "env";
      partial.aiBaseUrl = target.base_url || "";
      partial.aiAzureDeployment = target.deployment_name || "";
      partial.aiAzureApiVersion = target.api_version || "";
      partial.aiAwsRegion = target.region || "";
      const p = target.parameters || {};
      if (p.temperature !== undefined) partial.aiTemperature = p.temperature;
      if (p.max_output_tokens !== undefined || p.max_tokens !== undefined) {
        partial.aiMaxTokens = p.max_output_tokens ?? p.max_tokens;
      }
      if (p.top_p !== undefined) partial.aiTopP = p.top_p;
      if (target.timeout_seconds !== undefined) {
        partial.timeout = target.timeout_seconds;
        partial.aiTimeout = target.timeout_seconds;
      }
      if (target.timeout !== undefined) {
        partial.timeout = target.timeout;
        partial.aiTimeout = target.timeout;
      }
      if (target.name) partial.name = target.name;
    } else if (
      target.type === "python" ||
      target.type === "llamaindex" ||
      target.type === "llama-index" ||
      target.type === "llama_index" ||
      target.type === "langgraph" ||
      target.type === "lang-graph" ||
      target.type === "lang_graph" ||
      target.type === "autogen" ||
      target.type === "auto-gen" ||
      target.type === "auto_gen" ||
      target.type === "crewai" ||
      target.type === "crew-ai" ||
      target.type === "crew_ai" ||
      target.type === "openai-agents" ||
      target.type === "openai_agents" ||
      target.type === "openai-agent" ||
      target.type === "openai_agent" ||
      target.type === "google-adk" ||
      target.type === "google_adk" ||
      target.type === "google-genai" ||
      target.type === "google_genai" ||
      (typeof target.provider_id === "string" && (target.provider_id.startsWith("file://") || target.provider_id.endsWith(".py")))
    ) {
      const rawType = (target.type || "").toLowerCase();
      const rawPath = String(target.file_path || target.filePath || target.provider_id || "").toLowerCase();
      let detectedFramework: "python" | "llamaindex" | "langgraph" | "autogen" | "crewai" | "openai-agents" | "google-adk" = "python";

      if (rawType.includes("llamaindex") || rawPath.includes("llamaindex")) {
        detectedFramework = "llamaindex";
      } else if (rawType.includes("langgraph") || rawPath.includes("langgraph")) {
        detectedFramework = "langgraph";
      } else if (rawType.includes("autogen") || rawPath.includes("autogen")) {
        detectedFramework = "autogen";
      } else if (rawType.includes("crewai") || rawPath.includes("crewai")) {
        detectedFramework = "crewai";
      } else if (
        rawType.includes("openai-agent") ||
        rawType.includes("openai_agent") ||
        rawPath.includes("openai_agent") ||
        rawPath.includes("openai-agent")
      ) {
        detectedFramework = "openai-agents";
      } else if (
        rawType.includes("google-adk") ||
        rawType.includes("google_adk") ||
        rawType.includes("google-genai") ||
        rawType.includes("google_genai") ||
        rawPath.includes("google_adk") ||
        rawPath.includes("google-adk") ||
        rawPath.includes("google_agent")
      ) {
        detectedFramework = "google-adk";
      }

      partial.targetType = detectedFramework;
      partial.selectedCardId = detectedFramework;
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;

      let cfgJson = target.config_json || target.configJson || "";
      if (!cfgJson && (target.config || target.parameters)) {
        try {
          cfgJson = JSON.stringify(target.config || target.parameters, null, 2);
        } catch {
          cfgJson = "{}";
        }
      }
      if (!cfgJson) cfgJson = "{}";

      const defaultFwPath =
        detectedFramework === "llamaindex"
          ? "file://./llamaindex_agent.py"
          : detectedFramework === "langgraph"
          ? "file://./langgraph_agent.py"
          : detectedFramework === "autogen"
          ? "file://./autogen_agent.py"
          : detectedFramework === "crewai"
          ? "file://./crewai_agent.py"
          : detectedFramework === "openai-agents"
          ? "file://./openai_agent.py"
          : detectedFramework === "google-adk"
          ? "file://./google_adk_agent.py"
          : "/path/to/custom_provider.py";

      partial.pythonConfig = {
        filePath: target.file_path || target.filePath || target.provider_id || defaultFwPath,
        pythonExecutable: target.python_executable || target.pythonExecutable || target.config?.pythonExecutable || target.parameters?.pythonExecutable || "",
        configJson: cfgJson,
        testGenerationInstructions: target.test_generation_instructions || target.testGenerationInstructions || "",
        delayMs: target.delay_ms ?? target.delayMs ?? 0,
        extensionHook: target.extension_hook || target.extensionHook || "",
        prompts: target.prompts && target.prompts.length > 0 ? target.prompts : ["{{prompt}}"],
      };
    } else if (target.type === "javascript") {
      partial.targetType = "javascript";
      partial.selectedCardId = "javascript";
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;
      partial.javascriptConfig = {
        filePath: target.file_path || target.filePath || "/path/to/custom_provider.js",
        configJson: target.config_json || target.configJson || "{}",
        testGenerationInstructions: target.test_generation_instructions || target.testGenerationInstructions || "",
        delayMs: target.delay_ms ?? target.delayMs ?? 0,
        extensionHook: target.extension_hook || target.extensionHook || "",
        prompts: target.prompts && target.prompts.length > 0 ? target.prompts : ["{{prompt}}"],
      };
    } else if (target.type === "go") {
      partial.targetType = "go";
      partial.selectedCardId = "go";
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;
      partial.goConfig = {
        filePath: target.file_path || target.filePath || "/path/to/your/script",
        configJson: target.config_json || target.configJson || "{}",
        testGenerationInstructions: target.test_generation_instructions || target.testGenerationInstructions || "",
        delayMs: target.delay_ms ?? target.delayMs ?? 0,
        extensionHook: target.extension_hook || target.extensionHook || "",
        prompts: target.prompts && target.prompts.length > 0 ? target.prompts : ["{{prompt}}"],
      };
    } else if (target.type === "a2a") {
      partial.targetType = "a2a";
      partial.selectedCardId = "a2a";
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;
      const aAuth: any = target.authentication || {};
      partial.a2aConfig = {
        providerId: target.provider_id || target.providerId || "a2a",
        agentCardUrl: target.agent_card_url || target.agentCardUrl || "",
        endpointUrl: target.endpoint_url || target.endpointUrl || "",
        authType: (aAuth.type as A2AAuthType) || "none",
        bearerToken: aAuth.token || "",
        apiKeyName: aAuth.key || "X-API-Key",
        apiKeyValue: aAuth.value || "",
        basicUser: aAuth.username || "",
        basicPass: aAuth.password || "",
        configJson: target.config_json || target.configJson || "{}",
        testGenerationInstructions: target.test_generation_instructions || target.testGenerationInstructions || "",
        delayMs: target.delay_ms ?? target.delayMs ?? 0,
        extensionHook: target.extension_hook || target.extensionHook || "",
      };
    } else if (target.type === "claude-agent" || target.type === "claude-agent-sdk") {
      partial.targetType = "claude-agent";
      partial.selectedCardId = "claude-agent";
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;

      let toolMode: "preset" | "custom" | "disabled" = "preset";
      let toolsList: string[] = [];
      if (Array.isArray(target.tools)) {
        if (target.tools.length === 0) {
          toolMode = "disabled";
        } else {
          toolMode = "custom";
          toolsList = target.tools;
        }
      }

      let mcpJsonStr = "";
      if (target.mcp && typeof target.mcp === "object") {
        mcpJsonStr = JSON.stringify(target.mcp, null, 2);
      }

      partial.claudeAgentConfig = {
        providerId: target.provider_id || target.providerId || "anthropic:claude-agent-sdk",
        workingDir: target.working_dir || target.workingDir || "./",
        copyWorkingDir: target.copy_working_dir !== undefined ? Boolean(target.copy_working_dir) : (target.copyWorkingDir !== undefined ? Boolean(target.copyWorkingDir) : false),
        model: target.model || "claude-3-5-sonnet-20241022",
        fallbackModel: target.fallback_model || target.fallbackModel || "",
        apiKey: target.credentials?.api_key || target.credentials?.apiKey || target.api_key || target.apiKey || "",
        apiKeyRequired: target.api_key_required !== undefined ? Boolean(target.api_key_required) : (target.apiKeyRequired !== undefined ? Boolean(target.apiKeyRequired) : true),
        maxTurns: target.max_turns ?? target.maxTurns ?? 30,
        maxThinkingTokens: target.max_thinking_tokens ?? target.maxThinkingTokens ?? "",
        maxBudgetUsd: target.max_budget_usd ?? target.maxBudgetUsd ?? "",
        tokenBudget: target.task_budget?.total ?? target.taskBudget?.total ?? "",
        permissionMode: target.permission_mode || target.permissionMode || "default",
        allowDangerouslySkipPermissions: Boolean(target.allow_dangerously_skip_permissions ?? target.allowDangerouslySkipPermissions),
        customSystemPrompt: target.custom_system_prompt || target.customSystemPrompt || "",
        appendSystemPrompt: target.append_system_prompt || target.appendSystemPrompt || "",
        excludeDynamicSections: Boolean(target.exclude_dynamic_sections ?? target.excludeDynamicSections),
        toolsMode: toolMode,
        allowAllTools: Boolean(target.allow_all_tools ?? target.allowAllTools),
        customAllowedTools: target.custom_allowed_tools || target.customAllowedTools || toolsList || [],
        appendAllowedTools: target.append_allowed_tools || target.appendAllowedTools || [],
        disallowedTools: target.disallowed_tools || target.disallowedTools || [],
        additionalDirectories: target.additional_directories || target.additionalDirectories || [],
        mcpJson: mcpJsonStr,
        skills: Array.isArray(target.skills) ? target.skills : (typeof target.skills === "string" ? [target.skills] : []),
        settingSources: target.setting_sources || target.settingSources || [],
        configJson: target.config_json || target.configJson || "{}",
        testGenerationInstructions: target.test_generation_instructions || target.testGenerationInstructions || "",
        delayMs: target.delay_ms ?? target.delayMs ?? 0,
        extensionHook: target.extension_hook || target.extensionHook || "",
        prompts: target.prompts && target.prompts.length > 0 ? target.prompts : ["{{prompt}}"],
      };
    } else if (target.type === "mcp") {
      partial.targetType = "mcp";
      partial.selectedCardId = "mcp";
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;
      partial.mcpConfig = {
        serverConfig: target.server_config || target.serverConfig || "mcp",
        configJson: target.config_json || target.configJson || "{}",
        testGenerationInstructions: target.test_generation_instructions || target.testGenerationInstructions || "",
        delayMs: target.delay_ms ?? target.delayMs ?? 0,
        extensionHook: target.extension_hook || target.extensionHook || "",
        prompts: target.prompts && target.prompts.length > 0 ? target.prompts : ["{{prompt}}"],
      };
    } else if (
      target.type === "open-interpreter" ||
      target.type === "open_interpreter" ||
      target.type === "openinterpreter"
    ) {
      partial.targetType = "open-interpreter";
      partial.selectedCardId = "open-interpreter";
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;
      partial.openInterpreterConfig = {
        model: target.model || "gpt-4o",
        autoRun: target.auto_run !== undefined ? target.auto_run : (target.autoRun !== undefined ? target.autoRun : true),
        safeMode: (target.safe_mode as any) || (target.safeMode as any) || "off",
        maxOutput: target.max_output ?? target.maxOutput ?? 2000,
        systemMessage: target.system_message || target.systemMessage || "",
        apiBase: target.api_base || target.apiBase || "",
        apiKey: target.credentials?.api_key || target.credentials?.apiKey || "",
        configJson: target.config_json || target.configJson || "{}",
        testGenerationInstructions: target.test_generation_instructions || target.testGenerationInstructions || "",
        delayMs: target.delay_ms ?? target.delayMs ?? 0,
        extensionHook: target.extension_hook || target.extensionHook || "",
        prompts: target.prompts && target.prompts.length > 0 ? target.prompts : ["{{prompt}}"],
      };
    } else if (
      target.type === "ollama" ||
      target.type === "llamacpp" ||
      target.type === "llama-cpp" ||
      target.type === "llama.cpp" ||
      target.type === "llamafile" ||
      target.type === "localai" ||
      target.type === "text-generation-webui" ||
      target.type === "vllm" ||
      (typeof target.provider_id === "string" && (
        target.provider_id.startsWith("ollama:") ||
        target.provider_id.startsWith("llama.cpp:") ||
        target.provider_id.startsWith("localai:")
      ))
    ) {
      const detectedType =
        target.type === "ollama" || target.provider_id?.startsWith("ollama:")
          ? "ollama"
          : target.type === "llamacpp" || target.type === "llama-cpp" || target.type === "llama.cpp" || target.provider_id?.startsWith("llama.cpp:")
          ? "llamacpp"
          : target.type === "llamafile"
          ? "llamafile"
          : target.type === "localai" || target.provider_id?.startsWith("localai:")
          ? "localai"
          : target.type === "text-generation-webui"
          ? "text-generation-webui"
          : target.type === "vllm"
          ? "vllm"
          : "ollama";

      partial.targetType = detectedType;
      partial.selectedCardId = detectedType === "llamacpp" ? "llama-cpp" : detectedType;
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;

      let configJson = target.config_json || target.configJson || "";
      if ((!configJson || configJson === "{}") && target.config) {
        configJson = JSON.stringify(target.config, null, 2);
      } else if ((!configJson || configJson === "{}") && target.parameters) {
        configJson = JSON.stringify(target.parameters, null, 2);
      } else if (!configJson) {
        configJson = "{}";
      }

      const modelVal =
        target.provider_id ||
        target.providerId ||
        (target.model ? `${detectedType}:${target.model}` : "") ||
        (detectedType === "ollama"
          ? "ollama:llama3"
          : detectedType === "llamacpp"
          ? "llama.cpp:http://localhost:8080/completion"
          : detectedType === "llamafile"
          ? "LLaMA_CPP"
          : detectedType === "localai"
          ? "localai:chat:gpt-4"
          : detectedType === "text-generation-webui"
          ? "default"
          : "meta-llama/Llama-2-7b-chat-hf");

      const serverUrlVal =
        target.endpoint ||
        target.base_url ||
        target.host ||
        target.server_url ||
        target.serverUrl ||
        (detectedType === "ollama"
          ? "http://localhost:11434"
          : detectedType === "llamacpp"
          ? "http://localhost:8080/completion"
          : detectedType === "llamafile"
          ? "http://localhost:8080/v1"
          : detectedType === "localai"
          ? "http://localhost:8080/v1"
          : detectedType === "text-generation-webui"
          ? "http://localhost:5000/v1"
          : "http://localhost:8000/v1");

      const temp = target.temperature ?? target.config?.temperature ?? target.parameters?.temperature;
      const topP = target.top_p ?? target.topP ?? target.config?.top_p ?? target.parameters?.top_p;
      const topK = target.top_k ?? target.topK ?? target.config?.top_k ?? target.parameters?.top_k;
      const numPredict = target.num_predict ?? target.numPredict ?? target.config?.num_predict ?? target.parameters?.num_predict;
      const numCtx = target.num_ctx ?? target.numCtx ?? target.config?.num_ctx ?? target.parameters?.num_ctx;
      const nPredict = target.n_predict ?? target.nPredict ?? target.config?.n_predict ?? target.parameters?.n_predict;
      const maxTokens = target.max_tokens ?? target.maxTokens ?? target.config?.max_tokens ?? target.parameters?.max_tokens;
      const repeatPenalty = target.repeat_penalty ?? target.repeatPenalty ?? target.config?.repeat_penalty ?? target.parameters?.repeat_penalty;
      const keepAlive = target.keep_alive ?? target.keepAlive ?? target.config?.keep_alive ?? target.parameters?.keep_alive;
      const stopSeq = target.stop ?? target.stopSequences ?? target.config?.stop ?? target.parameters?.stop;
      const apiKey = target.api_key ?? target.apiKey ?? target.config?.apiKey ?? target.parameters?.apiKey ?? target.authentication?.token ?? target.authentication?.value ?? "";
      const apiKeyEnv = target.api_key_env ?? target.apiKeyEnv ?? target.config?.apiKeyEnv ?? target.credentials?.api_key_env ?? "";
      const thinking = target.thinking !== undefined ? Boolean(target.thinking) : target.thinkingMode !== undefined ? Boolean(target.thinkingMode) : (target.config?.thinking !== undefined ? Boolean(target.config.thinking) : undefined);
      const mode = target.mode || target.generationMode || target.config?.mode || target.parameters?.mode;
      const instructionTemplate = target.instruction_template || target.instructionTemplate || target.config?.instruction_template || target.parameters?.instruction_template;

      let pMode: "chat" | "completion" | "embeddings" = "chat";
      if (target.provider_mode === "completion" || target.providerMode === "completion" || target.provider_id?.includes(":completion:")) {
        pMode = "completion";
      } else if (target.provider_mode === "embeddings" || target.providerMode === "embeddings" || target.provider_id?.includes(":embeddings:")) {
        pMode = "embeddings";
      }

      const localValues: LocalModelTargetConfigValues = {
        modelName: modelVal,
        providerMode: pMode,
        serverUrl: serverUrlVal,
        apiKey: apiKey || "",
        apiKeyEnv: apiKeyEnv || "",
        ...(temp !== undefined ? { temperature: temp } : {}),
        ...(topP !== undefined ? { topP } : {}),
        ...(topK !== undefined ? { topK } : {}),
        ...(numPredict !== undefined ? { numPredict } : {}),
        ...(numCtx !== undefined ? { numCtx } : {}),
        ...(nPredict !== undefined ? { nPredict } : {}),
        ...(maxTokens !== undefined ? { maxTokens } : {}),
        ...(repeatPenalty !== undefined ? { repeatPenalty } : {}),
        ...(keepAlive !== undefined ? { keepAlive } : {}),
        ...(stopSeq !== undefined ? { stopSequences: stopSeq } : {}),
        ...(thinking !== undefined ? { thinkingMode: thinking } : {}),
        ...(mode !== undefined ? { generationMode: mode } : {}),
        ...(instructionTemplate !== undefined ? { instructionTemplate } : {}),
        configJson: configJson,
        testGenerationInstructions: target.test_generation_instructions || target.testGenerationInstructions || "",
        delayMs: target.delay_ms ?? target.delayMs ?? 0,
        extensionHook: target.extension_hook || target.extensionHook || "",
        prompts: target.prompts && target.prompts.length > 0 ? target.prompts : ["{{prompt}}"],
      };

      if (detectedType === "ollama") partial.ollamaConfig = localValues;
      else if (detectedType === "llamacpp") partial.llamaCppConfig = localValues;
      else if (detectedType === "llamafile") partial.llamafileConfig = localValues;
      else if (detectedType === "localai") partial.localaiConfig = localValues;
      else if (detectedType === "text-generation-webui") partial.textGenWebuiConfig = localValues;
      else if (detectedType === "vllm") partial.vllmConfig = localValues;
    } else if (
      target.type === "bedrock-agent" ||
      target.type === "bedrock_agent" ||
      target.type === "bedrock-agents" ||
      (typeof target.provider_id === "string" && target.provider_id.startsWith("bedrock-agent:"))
    ) {
      partial.targetType = "bedrock-agent";
      partial.selectedCardId = "bedrock-agents";
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;

      let extractedAgentId = target.agent_id || target.agentId || "";
      if (!extractedAgentId && typeof target.provider_id === "string" && target.provider_id.startsWith("bedrock-agent:")) {
        extractedAgentId = target.provider_id.split("bedrock-agent:")[1].trim();
      }

      const params = target.parameters || target.config || {};
      const agentAliasId = target.agent_alias_id || target.agentAliasId || params.agentAliasId || params.agent_alias_id || "TSTALIASID";
      const region = target.region || params.region || "us-east-1";
      const sessionId = target.session_id || target.sessionId || params.sessionId || params.session_id || "";
      const memoryId = target.memory_id || target.memoryId || params.memoryId || params.memory_id || "";
      const enableTrace = target.enable_trace !== undefined ? Boolean(target.enable_trace) : (params.enableTrace !== undefined ? Boolean(params.enableTrace) : false);
      const endSession = target.end_session !== undefined ? Boolean(target.end_session) : (params.endSession !== undefined ? Boolean(params.endSession) : false);
      const awsProfile = target.aws_profile || target.awsProfile || params.profile || params.awsProfile || "";

      let sessionAttrsJson = "{}";
      const sAttrs = target.session_attributes || target.sessionAttributes || params.sessionAttributes || params.session_attributes;
      if (sAttrs && typeof sAttrs === "object") {
        sessionAttrsJson = JSON.stringify(sAttrs, null, 2);
      }

      let promptSessionAttrsJson = "{}";
      const pAttrs = target.prompt_session_attributes || target.promptSessionAttributes || params.promptSessionAttributes || params.prompt_session_attributes;
      if (pAttrs && typeof pAttrs === "object") {
        promptSessionAttrsJson = JSON.stringify(pAttrs, null, 2);
      }

      let configJson = target.config_json || target.configJson || "";
      if (!configJson && target.config) {
        configJson = JSON.stringify(target.config, null, 2);
      } else if (!configJson && target.parameters) {
        configJson = JSON.stringify(target.parameters, null, 2);
      } else if (!configJson) {
        configJson = "{}";
      }

      let authMethod: "default" | "profile" | "explicit" = "default";
      if (awsProfile) {
        authMethod = "profile";
      } else if (target.aws_access_key_id || target.accessKeyId || target.credentials?.access_key_id) {
        authMethod = "explicit";
      }

      partial.bedrockAgentConfig = {
        agentId: extractedAgentId,
        agentAliasId,
        region,
        authMethod,
        awsProfile,
        accessKeyId: target.aws_access_key_id || target.accessKeyId || target.credentials?.access_key_id || "",
        secretAccessKey: target.aws_secret_access_key || target.secretAccessKey || target.credentials?.secret_access_key || "",
        sessionToken: target.aws_session_token || target.sessionToken || target.credentials?.session_token || "",
        sessionId,
        memoryId,
        enableTrace,
        endSession,
        timeout: target.timeout || 120,
        sessionAttributesJson: sessionAttrsJson,
        promptSessionAttributesJson: promptSessionAttrsJson,
        configJson,
        prompts: target.prompts && target.prompts.length > 0 ? target.prompts : ["{{prompt}}"],
        delayMs: target.delay_ms ?? target.delayMs ?? 0,
        extensionHook: target.extension_hook || target.extensionHook || "",
        testGenerationInstructions: target.test_generation_instructions || target.testGenerationInstructions || "",
      };
    } else if (
      target.type === "shell" ||
      target.type === "exec" ||
      target.type === "custom-script" ||
      target.type === "custom_script" ||
      target.type === "executable" ||
      (typeof target.provider_id === "string" && target.provider_id.startsWith("exec:")) ||
      (typeof (target as any).id === "string" && (target as any).id.startsWith("exec:")) ||
      (typeof target.endpoint === "string" && target.endpoint.startsWith("exec:"))
    ) {
      partial.targetType = "shell";
      partial.selectedCardId = "shell";
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;

      const providerId = (target.provider_id || (target as any).id || target.endpoint || "") as string;
      let cmd = target.command || target.executable || "";
      if (!cmd && providerId.startsWith("exec:")) {
        cmd = providerId;
      } else if (!cmd && providerId) {
        cmd = `exec:${providerId}`;
      }
      if (!cmd) cmd = "exec:python /path/to/agent.py";

      const cfg = target.config || target.parameters || {};
      const env = target.env || cfg.env || {};
      const timeout = target.timeout || cfg.timeout || 30000;

      let configJson = target.config_json || target.configJson || "";
      if (!configJson && Object.keys(cfg).length > 0) {
        configJson = JSON.stringify(cfg, null, 2);
      } else if (!configJson) {
        configJson = "{}";
      }

      partial.executableConfig = {
        command: cmd,
        configJson: configJson || "{}",
        env: typeof env === "object" && env !== null ? env : {},
        timeout: timeout,
        testGenerationInstructions: target.test_generation_instructions || target.testGenerationInstructions || "",
        prompts: target.prompts && target.prompts.length > 0 ? target.prompts : ["{{prompt}}"],
        delayMs: target.delay_ms ?? target.delayMs ?? 0,
        extensionHook: target.extension_hook || target.extensionHook || "",
      };
    } else if (target.type === "custom") {
      partial.targetType = "custom";
      partial.selectedCardId = "custom";
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;

      const rawConn = (target.connection_type || (target as any).connectionMethod || "http") as string;
      const normalizedConn: "http" | "python" | "custom_app" =
        rawConn === "executable" || rawConn === "custom_app"
          ? "custom_app"
          : rawConn === "python"
          ? "python"
          : "http";

      const auth: any = target.authentication || {};
      let headersList = [{ key: "", value: "" }];
      if (target.headers && typeof target.headers === "object") {
        const entries = Object.entries(target.headers).map(([key, value]) => ({
          key,
          value: String(value),
        }));
        if (entries.length > 0) {
          headersList = entries;
        }
      }

      partial.customTargetConfig = {
        connectionMethod: normalizedConn,
        endpoint: target.endpoint || "",
        method: target.method || "POST",
        headers: headersList,
        authType: (auth.type as any) || "none",
        bearerToken: auth.token || "",
        apiKey: auth.key || "",
        apiVal: auth.value || "",
        apiKeyLoc: auth.location || "header",
        basicUser: auth.username || "",
        basicPass: auth.password || "",
        bodyTemplate: target.body_template || "",
        responsePath: target.response_path || "",
        filePath: target.file_path || "file://./agent.py",
        functionName: target.function_name || "call_api",
        pythonExecutable: target.python_executable || "python3",
        command: target.command || "exec:python /path/to/agent.py",
        workingDir: target.working_dir || "",
        env: typeof target.env === "object" && target.env !== null ? target.env : {},
        configJson: target.config_json || "{}",
        timeout: target.timeout || 60,
      };
    } else if (target.type === "other-agent" || target.type === "other_agent") {
      partial.targetType = "other-agent";
      partial.selectedCardId = "other-agent";
      if (target.name) partial.name = target.name;
      if (target.timeout) partial.timeout = target.timeout;

      partial.otherAgentConfig = {
        framework: ((target.framework as any) || "langchain"),
        filePath: target.file_path || "file://./agent.py",
        functionName: target.function_name || "call_api",
        appId: target.app_id || "",
        endpoint: target.endpoint || "",
        command: target.command || "",
        pythonExecutable: target.python_executable || "python3",
        workingDir: target.working_dir || "",
        env: typeof target.env === "object" && target.env !== null ? target.env : {},
        configJson: target.config_json || "{}",
        timeout: target.timeout || 60,
      };
    } else {
      const normalizedType = target.type === "ws" ? "websocket" : (target.type || "http");
      partial.targetType = normalizedType;
      partial.selectedCardId =
        normalizedType === "websocket"
          ? "websocket"
          : normalizedType === "langchain"
          ? "langchain"
          : "http";
      if (target.name) partial.name = target.name;
      if (target.endpoint) partial.endpoint = target.endpoint;
      if (target.method) partial.httpMethod = target.method;
      if (target.timeout) partial.timeout = target.timeout;
    }
    if (target.body_template) {
      partial.bodyTemplate = typeof target.body_template === "string"
        ? target.body_template
        : JSON.stringify(target.body_template, null, 2);
    }
    if (target.response_path) partial.responsePath = target.response_path;
    if (target.input_key) partial.inputKey = target.input_key;
    if (target.response_key) partial.responseKey = target.response_key;
    if (target.protocols && Array.isArray(target.protocols)) {
      partial.protocols = target.protocols;
    }

    if (target.headers && typeof target.headers === "object") {
      partial.customHeaders = Object.entries(target.headers).map(([key, value]) => ({
        key,
        value: String(value),
      }));
    }

    if (target.authentication) {
      const auth = target.authentication;
      partial.authType = auth.type || "none";
      if (auth.token) partial.bearerToken = auth.token;
      if (auth.key) partial.apiKey = auth.key;
      if (auth.value) partial.apiVal = auth.value;
      if (auth.location) partial.apiKeyLoc = auth.location;
      if (auth.username) partial.basicUser = auth.username;
      if (auth.password) partial.basicPass = auth.password;
    }

    if (target.name) {
      partial.name = target.name;
      if (!partial.appName) partial.appName = target.name;
    }
  }

  if (config.application) {
    if (config.application.purpose) partial.appPurpose = config.application.purpose;
    const appNameVal = (config.application as any).name || config.application.description;
    if (appNameVal) {
      partial.appName = appNameVal;
      if (!partial.name) partial.name = appNameVal;
    }
    if (config.application.system_prompt) partial.appAdditionalContext = config.application.system_prompt;
  }

  if (config.model) {
    if (config.model.provider) partial.modelProvider = config.model.provider;
    if (config.model.name) partial.modelName = config.model.name;
    if (config.model.credential_ref) partial.modelApiKey = config.model.credential_ref;
    if (config.model.base_url) partial.modelBaseUrl = config.model.base_url;
    if (config.model.temperature !== undefined) partial.modelTemperature = config.model.temperature;
    if (config.model.max_tokens !== undefined) partial.modelMaxTokens = config.model.max_tokens;
    if (config.model.timeout !== undefined) partial.modelTimeout = config.model.timeout;
  }

  if (Array.isArray(config.plugins)) {
    const pluginMap: Record<string, boolean> = {};
    for (const p of config.plugins) {
      if (p) pluginMap[p] = true;
    }
    partial.selectedPlugins = pluginMap;
  }

  if (Array.isArray(config.strategies) && config.strategies.length > 0) {
    const strat = config.strategies[0];
    partial.selectedStrategy = strat.id;
    const cfg = strat.config || {};
    if (strat.id === "crescendo" && typeof cfg.max_turns === "number") {
      partial.crescendoMaxTurns = cfg.max_turns;
    } else if (strat.id === "hydra") {
      if (typeof cfg.max_branches === "number") partial.hydraBranches = cfg.max_branches;
      if (typeof cfg.max_depth === "number") partial.hydraDepth = cfg.max_depth;
    } else if ((strat.id === "mischievous-user" || strat.id === "mischievous_user") && typeof cfg.max_turns === "number") {
      partial.mischievousMaxTurns = cfg.max_turns;
    } else if (strat.id === "goat" && typeof cfg.max_turns === "number") {
      partial.goatMaxTurns = cfg.max_turns;
    } else if (strat.id === "jailbreak-templates" && typeof cfg.sample === "number") {
      partial.jailbreakSample = cfg.sample;
    } else if (strat.id === "layer" && Array.isArray(cfg.steps)) {
      partial.layerSteps = cfg.steps;
    } else if (strat.id === "unicode" && typeof cfg.mode === "string") {
      partial.unicodeMode = cfg.mode;
    } else if (strat.id === "best-of-n" && typeof cfg.n === "number") {
      partial.bestOfN = cfg.n;
    } else if (strat.id === "tree-jailbreak") {
      if (typeof cfg.branches === "number") partial.treeBranches = cfg.branches;
      if (typeof cfg.depth === "number") partial.treeDepth = cfg.depth;
    } else if (strat.id === "custom" && typeof cfg.template === "string") {
      partial.customTemplate = cfg.template;
    } else if (strat.id === "retry" && typeof cfg.max_retries === "number") {
      partial.maxRetries = cfg.max_retries;
    } else if (strat.id === "gcg") {
      if (typeof cfg.num_candidates === "number") partial.gcgNumCandidates = cfg.num_candidates;
      if (typeof cfg.custom_suffix === "string") partial.gcgCustomSuffix = cfg.custom_suffix;
    } else if (strat.id === "indirect-web-pwn" && typeof cfg.format === "string") {
      partial.indirectFormat = cfg.format;
    } else if (strat.id === "citation" && typeof cfg.style === "string") {
      partial.citationStyle = cfg.style;
    }
  }

  if (config.execution?.retry?.max_attempts !== undefined) {
    partial.maxRetries = config.execution.retry.max_attempts;
  }
  if (config.execution?.concurrency !== undefined) {
    partial.concurrency = config.execution.concurrency;
  }
  if (config.execution?.timeout_s !== undefined) {
    partial.timeout = config.execution.timeout_s;
  }
  if (config.execution?.budget?.max_usd !== undefined) {
    partial.budgetLimit = config.execution.budget.max_usd;
  } else if ((config.execution?.budget as any)?.max_cost_usd !== undefined) {
    partial.budgetLimit = (config.execution.budget as any).max_cost_usd;
  }

  return partial;
}

/**
 * Formats a scalar value for YAML serialization.
 */
function formatYamlScalar(val: any): string {
  if (typeof val === "boolean") return val ? "true" : "false";
  if (typeof val === "number") return String(val);
  if (typeof val === "string") {
    if (val.includes("\n")) {
      const indented = val
        .split("\n")
        .map((l) => (l ? `    ${l}` : ""))
        .join("\n");
      return `|\n${indented}`;
    }
    if (
      /[:#\[\]{},&*?|<>=!%@`]|^[-0-9]/.test(val) ||
      val === "" ||
      val === "true" ||
      val === "false" ||
      val === "null"
    ) {
      return JSON.stringify(val);
    }
    return val;
  }
  return String(val);
}

/**
 * Serializes a canonical RedTeamConfigDict to formatted YAML string.
 */
export function canonicalToYaml(obj: any, indent = 0): string {
  if (obj === null || obj === undefined) return "";
  const spaces = " ".repeat(indent);
  let out = "";

  if (Array.isArray(obj)) {
    for (const item of obj) {
      if (typeof item === "object" && item !== null) {
        const inner = canonicalToYaml(item, indent + 2).trimStart();
        out += `${spaces}- ${inner}`;
      } else {
        out += `${spaces}- ${formatYamlScalar(item)}\n`;
      }
    }
  } else if (typeof obj === "object") {
    for (const [key, value] of Object.entries(obj)) {
      if (value === null || value === undefined) continue;

      if (typeof value === "object") {
        if (Array.isArray(value)) {
          if (value.length === 0) {
            out += `${spaces}${key}: []\n`;
          } else {
            out += `${spaces}${key}:\n${canonicalToYaml(value, indent + 2)}`;
          }
        } else if (Object.keys(value).length === 0) {
          out += `${spaces}${key}: {}\n`;
        } else {
          out += `${spaces}${key}:\n${canonicalToYaml(value, indent + 2)}`;
        }
      } else {
        out += `${spaces}${key}: ${formatYamlScalar(value)}\n`;
      }
    }
  }
  return out;
}
